import React, {ReactElement, ReactNode} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {Formik} from 'formik';

import {CreateAdvisorAccount} from '../../../data/apis/Identity';
import useCreateAccount from '../../../data/identity/useCreateAccount/useCreateAccount';
import getIdentityError, {IdentityForm, IdentityErrors} from '../../../data/identity/errorMessages';

import CheckboxField from '../../DesignSystem/Fields/Checkbox/CheckboxWithFormikField';
import EmailAddressField from '../../DesignSystem/Fields/Email/EmailWithFormikField';
import PasswordField from '../../DesignSystem/Fields/Password/PasswordWithFormikField';
import PhoneNumberField from '../../DesignSystem/Fields/PhoneNumber/PhoneNumberWithFormikField';
import TermsAndConditions from '../../DesignSystem/TermsAndConditions/TermsAndConditions';
import TextWithFormikField from '../../DesignSystem/Fields/Text/TextWithFormikField';
import SelectWithFormikField from '../../DesignSystem/Fields/Select/SelectWithFormikField';
import IdentityErrorsModal from '../IdentityErrorsModal/IdentityErrorsModal';
import useShowModalOnCondition from '../../DesignSystem/BaseModal/useShowModalOnCondition';
import InlineFields from '../../DesignSystem/Fields/InlineFields/InlineFields';
import {Alignment, PartnerLegalStatus, PartnerType} from '../../../types';
import {Locale, useLocale} from '../../../RootProvider/LocaleContext';
import RenderOnLocales from '../../../RenderOnLocales';
import {isValidCompanyNumber, isValidKvkNumber} from '../../../formValidation';
import {getPageRoute} from '../../../utils/routes/getPageRoute';
import SinglePageForm from '../../DesignSystem/SinglePageForm/SinglePageForm';
import DynamicLink from '../../DynamicLink/DynamicLink';
import {HowItWorksId} from '../../Partners/HowDoesItWork/constants';
import {linkToPrivacyPolicy, linkToTermsOfService} from '../../../../i18n/formatters/helpers';
import Headers, {HeaderType} from '../../DesignSystem/Text/Headers/Headers';
import redirectByQueryParam from '../../../utils/redirectByQueryParam';

interface AllowedValues extends Omit<CreateAdvisorAccount, 'password' | 'marketingOptIn'> {}

function CreateAdvisorAccountForm({
  emailAddress = '',
  firstName = '',
  surname = '',
  companyName = '',
  companyNumber = '',
  partnerType = '',
  telephone = '',
}: Partial<AllowedValues>): ReactElement {
  const {formatMessage} = useIntl();
  const locale = useLocale();
  const {submit, isLoading, isUserCreated, error: identityError} = useCreateAccount();

  useShowModalOnCondition(
    <IdentityErrorsModal />,
    Boolean(identityError && identityError !== IdentityErrors.USERNAME_EXISTS)
  );

  if (isUserCreated) {
    redirectByQueryParam(getPageRoute('advisor.create.success', locale));
  }

  const marketingOptIn = false;
  const password = '';

  let emailAddressWarningMessage: string;
  if (identityError && identityError === IdentityErrors.USERNAME_EXISTS) {
    emailAddressWarningMessage = getIdentityError(identityError, formatMessage, IdentityForm.CREATE_ACCOUNT);
  }

  const isFormDisabled = false;

  return (
    <>
      <Headers alignment={Alignment.LEFT} headerType={HeaderType.H4}>
        <FormattedMessage
          id="partners.advisor.form.text.v2"
          values={{
            a: (chunks: ReactNode[]) => (
              <DynamicLink
                testId="advisor.create.howItWorks"
                href={`${getPageRoute('partners.advisor.home', locale)}/#${HowItWorksId}`}
              >
                {chunks}
              </DynamicLink>
            ),
          }}
        />
      </Headers>
      <Formik
        initialValues={{
          companyName,
          companyNumber,
          emailAddress,
          firstName,
          isAdvisor: true,
          marketingOptIn,
          partnerType,
          password,
          surname,
          telephone,
        }}
        onSubmit={submit}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({errors, handleSubmit}) => (
          <SinglePageForm
            errors={errors}
            onSubmit={handleSubmit}
            submitButtonLabel={formatMessage({id: 'phoenix.createAccount.form.button.text'})}
            submitButtonId="createAccount"
            isSubmitLoading={isLoading}
            afterSubmitContent={
              <TermsAndConditions>
                <FormattedMessage
                  id="phoenix.createAccount.form.termsAndConditions.v3"
                  values={{
                    linkToPrivacyPolicy: linkToPrivacyPolicy(locale),
                    linkToTermsOfService: linkToTermsOfService(locale),
                  }}
                />
              </TermsAndConditions>
            }
            isFormDisabled={isFormDisabled}
          >
            <TextWithFormikField<CreateAdvisorAccount>
              label={formatMessage({id: 'advisorAccount.form.companyName.label'})}
              name="companyName"
              placeholder={formatMessage({id: 'advisorAccount.form.companyName.placeholder'})}
              required
              isDisabled={isFormDisabled}
            />
            <SelectWithFormikField<CreateAdvisorAccount>
              label={formatMessage({id: 'advisorAccount.form.partnerType.label'})}
              name="partnerType"
              options={[
                {
                  label: formatMessage({id: 'advisorAccount.form.partnerType.options.advisoryAccount'}),
                  value: PartnerType.ADVISORY_ACCOUNT,
                },
                {
                  label: formatMessage({id: 'advisorAccount.form.partnerType.options.advisoryBroker'}),
                  value: PartnerType.ADVISORY_BROKER,
                },
                {
                  label: formatMessage({id: 'advisorAccount.form.partnerType.options.advisoryOther'}),
                  value: PartnerType.ADVISORY_OTHER,
                },
              ]}
              required
              isDisabled={isFormDisabled}
            />
            <SelectWithFormikField<CreateAdvisorAccount>
              label={formatMessage({id: 'advisorAccount.form.legalStatus.label'})}
              name="legalStatus"
              options={[
                {
                  label: formatMessage({id: 'advisorAccount.form.legalStatus.options.limitedCompany'}),
                  value: PartnerLegalStatus.LIMITED_COMPANY,
                },
                {
                  label: formatMessage({id: 'advisorAccount.form.legalStatus.options.soleTrader'}),
                  value: PartnerLegalStatus.SOLE_TRADER,
                },
                {
                  label: formatMessage({id: 'advisorAccount.form.legalStatus.options.limitedPartnership'}),
                  value: PartnerLegalStatus.LIMITED_PARTNERSHIP,
                },
                {
                  label: formatMessage({id: 'advisorAccount.form.legalStatus.options.partnershipThreeOrLess'}),
                  value: PartnerLegalStatus.PARTNERSHIP_THREE_OR_LESS,
                },
                {
                  label: formatMessage({id: 'advisorAccount.form.legalStatus.options.partnershipFourOrMore'}),
                  value: PartnerLegalStatus.PARTNERSHIP_FOUR_OR_MORE,
                },
                {
                  label: formatMessage({id: 'advisorAccount.form.legalStatus.options.other'}),
                  value: PartnerLegalStatus.OTHER,
                },
              ]}
              required
              isDisabled={isFormDisabled}
            />
            <RenderOnLocales locales={[Locale.uk]}>
              {/* Company Number */}
              <TextWithFormikField<CreateAdvisorAccount>
                label={formatMessage({id: 'phoenix.fields.companyNumber.label'})}
                name="companyNumber"
                placeholder={formatMessage({id: 'phoenix.fields.companyNumber.placeholder'})}
                validate={(value: string) =>
                  isValidCompanyNumber(value)
                    ? undefined
                    : formatMessage({id: 'phoenix.errors.companyNumber.validFormat'})
                }
                isDisabled={isFormDisabled}
              />
            </RenderOnLocales>
            <RenderOnLocales locales={[Locale.nl]}>
              {/* KVK Number */}
              <TextWithFormikField<CreateAdvisorAccount>
                label={formatMessage({id: 'phoenix.fields.KVKNumber.label'})}
                name="companyNumber"
                placeholder={formatMessage({id: 'phoenix.fields.KVKNumber.placeholder'})}
                validate={(value: string) =>
                  isValidKvkNumber(value) ? undefined : formatMessage({id: 'phoenix.errors.KVKNumber.validFormat'})
                }
                required
                isDisabled={isFormDisabled}
              />
            </RenderOnLocales>
            {/* placeholder for advisorType */}
            <InlineFields>
              <TextWithFormikField<CreateAdvisorAccount>
                label={formatMessage({id: 'phoenix.createAccount.form.firstName.label'})}
                name="firstName"
                placeholder={formatMessage({id: 'phoenix.fields.firstName.placeholder'})}
                required
                isDisabled={isFormDisabled}
              />
              <TextWithFormikField<CreateAdvisorAccount>
                label={formatMessage({id: 'phoenix.createAccount.form.surname.label'})}
                name="surname"
                placeholder={formatMessage({id: 'phoenix.fields.surname.placeholder'})}
                required
                isDisabled={isFormDisabled}
              />
            </InlineFields>
            <PhoneNumberField<CreateAdvisorAccount>
              name="telephone"
              required
              isDisabled={isFormDisabled}
              isMobilePhone
            />
            <EmailAddressField<CreateAdvisorAccount>
              label={formatMessage({id: 'phoenix.fields.email.label'})}
              name="emailAddress"
              placeholder={formatMessage({id: 'phoenix.fields.email.placeholder'})}
              required
              warningMessage={emailAddressWarningMessage}
              isDisabled={isFormDisabled}
            />
            <PasswordField<CreateAdvisorAccount>
              label={formatMessage({id: 'phoenix.fields.password.label'})}
              name="password"
              required
              isDisabled={isFormDisabled}
            />
            <CheckboxField<CreateAdvisorAccount>
              label={<FormattedMessage id="phoenix.createAccount.form.marketingOptIn.label.v3" />}
              name="marketingOptIn"
              isDisabled={isFormDisabled}
            />
          </SinglePageForm>
        )}
      </Formik>
    </>
  );
}

export default CreateAdvisorAccountForm;
